import React from 'react';
import {
  useToast,
  Spinner,
  Flex,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  SimpleGrid,
  Tooltip,
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Box,
  Image,
  Button,
  Divider,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useDisclosure,
  ModalBody,
  ModalHeader,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Alert,
  AlertIcon,
  Code,
  Link,
  HStack,
  VStack,
  Text,
  Progress,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Grid,
  GridItem,
  Tag,
} from '@chakra-ui/react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';
import {User} from 'auth0';
import {format, formatDistanceToNow} from 'date-fns';
import {Navigate, useNavigate, useParams} from 'react-router';
import {useKeyplayApi} from '../../context/ApiContext';
import {WithId} from '../../shared/util';
import {Link as ReactRouterLink} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {
  Customer,
  PlanLabels,
  getFeatures,
  getLimit,
} from '../../shared/customer';
import {CreateOrEditCustomerButton} from './CreateOrEditCustomerButton';
import {TrashCan} from '@carbon/icons-react';
import {UpdateCreditsModal} from './UpdateCreditsModal';
import {
  CustomerQueryKeys,
  useAddUserMutation,
  useCustomerDetails,
  useRemoveUserMutation,
} from './hooks/customer';
import {DeleteCustomerAccountsButton} from './DeleteCustomerAccountsButton';
import {ObjectId} from 'bson';
import {useUser} from '../../context/UserContext';
import {useAuth0} from '@auth0/auth0-react';
import _ from 'lodash';

function RemoveUserCellComponent({
  userId,
  userEmail,
  customer,
}: {
  userId: string;
  userEmail: string;
  customer: WithId<Customer>;
}) {
  const {isOpen, onOpen, onClose} = useDisclosure();

  return (
    <>
      <Button colorScheme="red" onClick={onOpen} variant="outline">
        <TrashCan />
      </Button>
      <RemoveUserModal
        userId={userId}
        userEmail={userEmail}
        customer={customer}
        isOpen={isOpen}
        onClose={onClose}
      />
    </>
  );
}

const StatCard = ({
  label,
  value,
  helpText,
  color = 'gray',
}: {
  label: string;
  value: string | number;
  helpText?: string | React.ReactNode;
  color?: string;
}) => (
  <Stat>
    <StatLabel fontSize="sm" color="gray.600">
      {label}
    </StatLabel>
    <StatNumber fontSize="lg" color={`${color}.400`}>
      {value}
    </StatNumber>
    {helpText && <StatHelpText fontSize="xs">{helpText}</StatHelpText>}
  </Stat>
);

const ProgressCard = ({
  label,
  current,
  max,
  color = 'blue',
}: {
  label: string;
  current: number;
  max: number;
  color?: string;
}) => {
  const percentage = max > 0 ? (current / max) * 100 : 0;
  const isNearLimit = percentage > 80;

  return (
    <Card variant="outline" size="sm" p={3}>
      <VStack align="stretch" spacing={2}>
        <Text fontSize="sm" fontWeight="medium" color="gray.600">
          {label}
        </Text>
        <VStack align="stretch" spacing={1}>
          <Flex justify="space-between" align="center">
            <Text fontSize="lg" fontWeight="bold">
              {current.toLocaleString()}
            </Text>
            <Text fontSize="sm" color="gray.500">
              of {max.toLocaleString()}
            </Text>
          </Flex>
          <Progress
            value={percentage}
            colorScheme={isNearLimit ? 'red' : color}
            size="sm"
            borderRadius="md"
          />
          <Text fontSize="xs" color="gray.500" textAlign="right">
            {percentage.toFixed(1)}% used
          </Text>
        </VStack>
      </VStack>
    </Card>
  );
};

const JsonDisplay = ({data, label}: {data: unknown; label: string}) => {
  const isEmpty = _.isEmpty(data);
  const jsonString = JSON.stringify(data, null, 2);

  return (
    <VStack align="stretch" spacing={2}>
      <Text fontSize="sm" fontWeight="medium" color="gray.600">
        {label}
      </Text>
      {isEmpty ? (
        <Text fontSize="xs" color="gray.500" fontStyle="italic">
          -
        </Text>
      ) : (
        <Code
          p={2}
          fontSize="xs"
          whiteSpace="pre-wrap"
          h="100%"
          maxH="200px"
          overflow="auto"
          bg="gray.50"
        >
          {jsonString}
        </Code>
      )}
    </VStack>
  );
};

export const CustomerDetails = () => {
  const user = useUser();
  const {loginWithRedirect} = useAuth0();
  const customerId = new ObjectId(useParams().customerId!);
  const {isOpen, onOpen, onClose} = useDisclosure();
  const updateCreditsModal = useDisclosure();
  const toast = useToast();
  const addUserToCustomer = useAddUserMutation();

  const customerDetails = useCustomerDetails(customerId);
  if (customerDetails.isPending) {
    return <Spinner />;
  }

  const customer = customerDetails.data?.customer;
  if (!customer) {
    toast({
      title: 'Unable to load customer',
      status: 'error',
    });

    return <Navigate to="/admin/provisioning" replace />;
  }

  const totalUsers = customerDetails.data?.users?.length ?? 0;
  const usersLimit = getLimit(customer, 'users');

  let plan = PlanLabels[customer.plan.type];
  const isTrialPlan =
    customer.plan.type === 'listBuilder' && customer.plan.trial;
  if (isTrialPlan) {
    plan += ' (Trial)';
  }

  const crmSyncDay =
    customer.integrations.hubspot?.crmSyncDay ??
    customer.integrations.salesforce?.crmSyncDay;

  return (
    <Flex direction="column">
      <Breadcrumb textColor="kgray.300" mb={5}>
        <BreadcrumbItem>
          <BreadcrumbLink as={ReactRouterLink} to="/admin/provisioning">
            Customers
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink isCurrentPage>{customer.name}</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>
      <Box>
        <Flex justifyContent="space-between" alignItems="center" mb={8}>
          <Flex alignItems="center">
            <Box fontSize="2xl" mr={2} mb={2}>
              {customer.name}
            </Box>
            <Box textColor="kgray.300" verticalAlign="center">
              ({customer._id.toString()})
            </Box>
          </Flex>
          <HStack>
            <DeleteCustomerAccountsButton customer={customer} />
            <CreateOrEditCustomerButton customer={customer} />
          </HStack>
        </Flex>
        <VStack spacing={6} align="stretch">
          {/* Basic Information Section */}
          <Card>
            <CardHeader pb={2}>
              <Heading size="md">Basic Information</Heading>
            </CardHeader>
            <CardBody pt={0}>
              <SimpleGrid columns={{base: 1, md: 2}} spacing={4}>
                <StatCard
                  label="Plan"
                  value={plan}
                  color={isTrialPlan ? 'korange' : 'kblue'}
                />
                <StatCard
                  label="Onboarded"
                  value={customer.onboarded ? 'Yes' : 'No'}
                  color={customer.onboarded ? 'kgreen' : 'kred'}
                />
              </SimpleGrid>
              {customer.churnedAt && (
                <Alert status="error" mt={4}>
                  <AlertIcon />
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="medium">Customer Churned</Text>
                    <Text fontSize="sm">
                      Churned: {format(customer.churnedAt, 'PPP')}
                      {customer.scheduledDeletionAt && (
                        <>
                          {' · '}
                          Deletion scheduled:{' '}
                          {format(customer.scheduledDeletionAt, 'PPP')}
                        </>
                      )}
                    </Text>
                  </VStack>
                </Alert>
              )}
              <UpdateCreditsModal
                currentCredits={customer.credits}
                customerId={customer._id}
                isOpen={updateCreditsModal.isOpen}
                onClose={updateCreditsModal.onClose}
              />
            </CardBody>
          </Card>

          {/* Configuration Section */}
          <Card>
            <CardHeader pb={3}>
              <Heading size="md">Configuration</Heading>
            </CardHeader>
            <CardBody pt={0}>
              <SimpleGrid columns={{base: 1, md: 2}} spacing={4}>
                <JsonDisplay data={getFeatures(customer)} label="Features" />
                <JsonDisplay
                  data={customer.signals ?? {}}
                  label="Scoring Signals"
                />
                <JsonDisplay data={customer.toggles ?? {}} label="Toggles" />
                <JsonDisplay data={customer.options ?? {}} label="Options" />
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* Usage & Limits Section */}
          <Card>
            <CardHeader pb={3}>
              <Heading size="md">Usage & Limits</Heading>
            </CardHeader>
            <CardBody pt={0}>
              <Grid
                templateColumns={{base: '1fr', md: 'repeat(4, 1fr)'}}
                gap={4}
              >
                <StatCard
                  label="Recommended"
                  value={getLimit(
                    customer,
                    'recommendedAccounts'
                  ).toLocaleString()}
                  color="kblue"
                />
                <StatCard
                  label="Active"
                  value={getLimit(customer, 'activeAccounts').toLocaleString()}
                  color="kblue"
                />
                <StatCard
                  label="Markets"
                  value={getLimit(customer, 'markets')}
                  color="kgray"
                />
                <StatCard
                  label="Credits"
                  value={customer.credits.toLocaleString()}
                  color="kgreen"
                  helpText={
                    <Link
                      color="kblue.300"
                      onClick={updateCreditsModal.onOpen}
                      fontSize="xs"
                    >
                      Update
                    </Link>
                  }
                />

                <GridItem colSpan={{base: 1, md: 2}}>
                  <ProgressCard
                    label="Saves"
                    current={customer.limits?.saves?.value ?? 0}
                    max={getLimit(customer, 'saves') ?? 0}
                    color="blue"
                  />
                </GridItem>
                <GridItem colSpan={{base: 1, md: 2}}>
                  <ProgressCard
                    label="Saves per Month"
                    current={customer.limits?.savesPerMonth?.value ?? 0}
                    max={getLimit(customer, 'savesPerMonth') ?? 0}
                    color="green"
                  />
                </GridItem>
                <GridItem colSpan={{base: 1, md: 2}}>
                  <ProgressCard
                    label="Web Intent IDs per Month"
                    current={
                      customer.limits?.webIntentIdentificationsPerMonth
                        ?.value ?? 0
                    }
                    max={
                      getLimit(customer, 'webIntentIdentificationsPerMonth') ??
                      0
                    }
                    color="purple"
                  />
                </GridItem>
              </Grid>
            </CardBody>
          </Card>

          {/* Integrations Section */}
          <Card>
            <CardHeader pb={3}>
              <Heading size="md">Integrations</Heading>
            </CardHeader>
            <CardBody pt={0}>
              <VStack align="stretch" spacing={4}>
                {_.values(customer.integrations).length > 0 ? (
                  <Flex wrap="wrap" gap={2}>
                    {_.values(customer.integrations).map((integration) => (
                      <Tag key={integration.id.toString()}>
                        {_.startCase(integration.type)}
                      </Tag>
                    ))}
                  </Flex>
                ) : (
                  <Text fontSize="sm" color="gray.500">
                    No integrations configured
                  </Text>
                )}
                {crmSyncDay && (
                  <SimpleGrid columns={{base: 1, md: 2}} spacing={4}>
                    <StatCard
                      label="CRM Sync Day"
                      value={crmSyncDay}
                      color="kblue"
                    />
                  </SimpleGrid>
                )}
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Box>
      <Divider my={8} />
      <AddUserModal customer={customer} isOpen={isOpen} onClose={onClose} />
      <Box>
        <Flex alignItems="center" gap={2} mb={4}>
          <Box flexGrow={1} fontSize="xl">
            Members{' '}
            <Box as="span" color="kgray.400" fontSize="md">
              ({totalUsers} / {usersLimit})
            </Box>
          </Box>
          <Button
            colorScheme="red"
            fontWeight="normal"
            isLoading={addUserToCustomer.isPending}
            onClick={() => {
              addUserToCustomer.mutate(
                {
                  email: user.auth0.email!,
                  customerId: customer._id,
                  isTemporary: true,
                },
                {
                  onSuccess: async () => {
                    await loginWithRedirect({
                      authorizationParams: {
                        customerId: customer._id,
                      },
                    });
                  },
                }
              );
            }}
            variant="ghost"
          >
            Login as Temp User
          </Button>
          <Button
            colorScheme="kbuttonblue"
            isDisabled={totalUsers >= usersLimit}
            onClick={onOpen}
            variant="solid"
          >
            Add User
          </Button>
        </Flex>
        {customerDetails.data?.users &&
          (customerDetails.data.users.length === 0 ? (
            <Box>
              <Flex alignItems="center" justifyContent="center" mt={4} mb={4}>
                This Customer has no members
              </Flex>
              <Flex alignItems="center" justifyContent="center" mt={4} mb={4}>
                <DeleteCustomerButton
                  customerId={customer._id}
                  customerName={customer.name}
                />
              </Flex>
            </Box>
          ) : (
            <MembersList
              users={customerDetails.data.users}
              customer={customer}
            />
          ))}
      </Box>
    </Flex>
  );
};

const membersColumnHelper = createColumnHelper<User>();
const MembersList = ({
  users,
  customer,
}: {
  users: User[];
  customer: WithId<Customer>;
}) => {
  const columns = [
    membersColumnHelper.accessor('picture', {
      header: () => null,
      cell: (info) => (
        <Image borderRadius="full" src={info.getValue()} w={10} h={10} />
      ),
    }),
    membersColumnHelper.accessor(
      (acc): {name?: string; email?: string} => {
        return {name: acc.name, email: acc.email};
      },
      {
        header: 'Name',
        cell: (info) => (
          <Box>
            <Box>{info.getValue().name ?? info.getValue().email}</Box>
            <Box textColor="kgray.300">{info.getValue().email}</Box>
          </Box>
        ),
      }
    ),
    membersColumnHelper.accessor('logins_count', {
      header: 'Logins',
      cell: (info) => info.getValue() ?? 0,
    }),
    membersColumnHelper.accessor('last_login', {
      header: 'Last Login',
      cell: (info) => {
        const value = info.getValue();
        return (
          <>
            {value ? (
              <Tooltip label={format(new Date(value), 'PPpp')}>
                <Box>
                  {formatDistanceToNow(new Date(value), {
                    addSuffix: true,
                  })}
                </Box>
              </Tooltip>
            ) : (
              'never'
            )}
          </>
        );
      },
    }),
    membersColumnHelper.accessor(
      (acc): {id?: string; email?: string} => {
        return {id: acc.user_id, email: acc.email};
      },
      {
        id: 'Remove User',
        header: () => null,
        cell: (info) => (
          <RemoveUserCellComponent
            userId={info.getValue().id ?? ''}
            userEmail={info.getValue().email ?? ''}
            customer={customer}
          />
        ),
      }
    ),
  ];

  const table = useReactTable({
    data: users,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <TableContainer>
        <Table variant="striped">
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Td key={cell.id} py={4}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </>
  );
};

const AddUserModal = ({
  customer,
  isOpen,
  onClose,
}: {
  customer: WithId<Customer>;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const {
    formState: {errors},
    getValues,
    handleSubmit,
    register,
    reset,
  } = useForm<{email: string}>();
  const toast = useToast();
  const addUserToCustomer = useAddUserMutation();

  const onHandleSubmit = async () => {
    const userEmail = getValues().email;
    addUserToCustomer.mutate(
      {email: userEmail, customerId: customer._id},
      {
        onSuccess: () => {
          toast({
            title: 'User added!',
            // The Auth0 search API is eventually consistent, so we might not be able to show the new user right away.
            description:
              'If the new user does not show in the members list, try refreshing the page.',
            status: 'success',
            position: 'bottom',
          });

          reset();
          onClose();
        },
      }
    );
  };

  return (
    <Modal
      isCentered={true}
      isOpen={isOpen}
      onClose={() => {
        reset();
        onClose();
      }}
      size="md"
    >
      <ModalOverlay />
      <ModalContent py={4} px={2}>
        <ModalHeader textAlign="center" fontSize="xl" fontWeight="normal">
          Add User to{' '}
          <Box as="span" fontWeight="bold">
            {customer.name}
          </Box>
        </ModalHeader>
        <ModalBody display="flex" flexDirection="column" gap={4}>
          <Box>Enter the email address of the user to invite.</Box>
          <Alert status="warning" mb={4}>
            <AlertIcon />
            Only new users will be sent an email.
          </Alert>
          <form onSubmit={handleSubmit(onHandleSubmit)}>
            <FormControl isInvalid={!!errors.email}>
              <FormLabel htmlFor="email">Email</FormLabel>
              <Input
                autoComplete="off"
                type="email"
                {...register('email', {required: true})}
              />
              <FormErrorMessage>
                {errors.email && 'Email is required'}
              </FormErrorMessage>
            </FormControl>
            <Button
              colorScheme="kbuttonblue"
              disabled={addUserToCustomer.isPending}
              isLoading={addUserToCustomer.isPending}
              mt={4}
              type="submit"
              variant="solid"
              w="full"
            >
              Invite
            </Button>
          </form>
        </ModalBody>
        <ModalCloseButton />
      </ModalContent>
    </Modal>
  );
};

const RemoveUserModal = ({
  userId,
  userEmail,
  customer,
  isOpen,
  onClose,
}: {
  userId: string;
  userEmail: string;
  customer: WithId<Customer>;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const toast = useToast();
  const removeUserFromCustomer = useRemoveUserMutation();

  const onHandleSubmit = async () => {
    removeUserFromCustomer.mutate(
      {userId, customerId: customer._id},
      {
        onSuccess: () => {
          toast({
            title: 'User removed',
            // The Auth0 search API is eventually consistent, so we might not be able to display the removed user right away.
            description:
              'If the user does not disappear from the members list, try refreshing the page.',
            status: 'success',
            position: 'bottom',
          });

          onClose();
        },
      }
    );
  };

  return (
    <Modal
      isCentered={true}
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}
      size="md"
    >
      <ModalOverlay />
      <ModalContent py={4} px={2}>
        <ModalHeader textAlign="center" fontSize="xl" fontWeight="normal">
          Remove user{' '}
          <Box as="span" fontWeight="bold">
            {userEmail}
          </Box>
          {' from '}
          <Box as="span" fontWeight="bold">
            {customer.name}
          </Box>
        </ModalHeader>
        <ModalBody display="flex" flexDirection="column" gap={4}>
          <Button
            colorScheme="kred"
            disabled={removeUserFromCustomer.isPending}
            isLoading={removeUserFromCustomer.isPending}
            mt={4}
            type="submit"
            variant="solid"
            w="full"
            onClick={onHandleSubmit}
          >
            Remove
          </Button>
        </ModalBody>
        <ModalCloseButton />
      </ModalContent>
    </Modal>
  );
};

function DeleteCustomerButton({
  customerId,
  customerName,
}: {
  customerId: ObjectId;
  customerName: string;
}) {
  const {isOpen, onOpen, onClose} = useDisclosure();
  return (
    <>
      <Button colorScheme="kred" onClick={onOpen} variant="solid">
        Delete Customer
      </Button>
      <DeleteCustomerModal
        customerId={customerId}
        customerName={customerName}
        isOpen={isOpen}
        onClose={onClose}
      />
    </>
  );
}

const DeleteCustomerModal = ({
  customerId,
  customerName,
  isOpen,
  onClose,
}: {
  customerId: ObjectId;
  customerName: string;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const makeApiCall = useKeyplayApi();
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  const deleteCustomer = useMutation({
    mutationFn: ({customerId}: {customerId: ObjectId}) =>
      makeApiCall(
        '/debug/customer/deleteCustomer',
        {method: 'POST', data: {customerId}},
        {toastOnError: true}
      ),
    onSuccess: async () => {
      toast({
        title: 'Customer deleted',
        description:
          'If the customer does not disappear from the list, try refreshing the page.',
        status: 'success',
        position: 'bottom',
      });
      queryClient.removeQueries({
        queryKey: CustomerQueryKeys.list(),
      });
      navigate('/admin/provisioning', {replace: true});
      onClose();
    },
  });

  const onHandleSubmit = async () => {
    deleteCustomer.mutate({customerId: customerId});
  };

  return (
    <Modal
      isCentered={true}
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}
      size="md"
    >
      <ModalOverlay />
      <ModalContent py={4} px={2}>
        <ModalHeader textAlign="center" fontSize="xl" fontWeight="normal">
          Delete{' '}
          <Box as="span" fontWeight="bold">
            {customerName}
          </Box>
        </ModalHeader>
        <ModalBody display="flex" flexDirection="column" gap={4}>
          <Button
            colorScheme="kred"
            disabled={deleteCustomer.isPending}
            isLoading={deleteCustomer.isPending}
            mt={4}
            type="submit"
            variant="solid"
            w="full"
            onClick={onHandleSubmit}
          >
            Delete
          </Button>
        </ModalBody>
        <ModalCloseButton />
      </ModalContent>
    </Modal>
  );
};
